import { FormField, RequestStatus, Streamfield, WagtailImageRendition, WagtailSvg } from '@/types';
import BannerImage from '@components/BannerImage';
import Button from '@components/Button';
import EmailField from '@components/Form/FormFields/EmailField';
import Honeypot from '@components/Form/Honeypot';
import SelectField from '@components/Form/FormFields/SelectField';
import SingleLineField from '@components/Form/FormFields/SingleLineField';
import Hero from '@components/Hero';
import RawHtml from '@components/RawHtml';
import { BasePageProps, basePageWrap } from '@views/BasePage';
import { useState } from 'react';
import {
  getFooter,
  getHeader,
  getMenu,
  getOpeningHours,
  getPage,
  WagtailApiResponseError,
} from '@api/wagtail';
import { GetStaticProps } from 'next';
import { loaders } from '@/streamfields';
import Streamfields from '@streamfields/Streamfields';
import clsx from 'clsx';
import CheckBoxField from '@components/Form/FormFields/CheckBoxField';
import NumberField from '@components/Form/FormFields/NumberField';
import RadioField from '@components/Form/FormFields/RadioField';
import Checkbox from '@components/Form/Checkbox';

type FormApiErrors = Record<string, string[]>;

type Props = {
  svg: WagtailSvg;
  title: string;
  subheader: string;
  subtitle: string;
  before_form: Streamfield[];
  after_form: Streamfield[];
  thank_you_text: string;
  form_fields: FormField[];
  banner_image: WagtailImageRendition | null;
  mobile_banner_image: WagtailImageRendition | null;
} & BasePageProps;

const KUNDENTYP_PRIVAT = 'Privatkunde';
const KUNDENTYP_GESCHAEFT = 'Geschäftskunde';
const KUNDENSTATUS_BESTAND = 'Ich bin bereits Kunde der Stadtwerke Witten GmbH';
const KUNDENSTATUS_NEU = 'Ich bin noch kein Kunde der Stadtwerke Witten GmbH';

function LadekarteBestellenPage(props: Props) {
  const {
    title = '',
    subheader,
    subtitle,
    form_fields: formFields,
    before_form: beforeForm,
    after_form: afterForm,
    thank_you_text: thankYouText,
    banner_image: bannerImage,
    mobile_banner_image: mobileBannerImage,
    svg,
  } = props;

  const [status, setStatus] = useState<RequestStatus>('idle');
  const [errors, setErrors] = useState<FormApiErrors | null>(null);
  const [kundentyp, setKundentyp] = useState<string>(KUNDENTYP_PRIVAT);
  const [kundenstatus, setKundenstatus] = useState<string>(KUNDENSTATUS_NEU);
  const [ladekarte, setLadekarte] = useState<boolean>(false);
  const [ladekarteRoaming, setLadekarteRoaming] = useState<boolean>(false);
  const [productError, setProductError] = useState<boolean>(false);
  const [wasSubmitted, setWasSubmitted] = useState<boolean>(false);

  // this page only works with corresponding wagtail page, to remove, make sure to remove the wagtailpage but also this site here.
  // uses form fields of the wagtailpage at /ladekarte-bestellen
  // cleannames of forminputs in backendpage are relevant to handle the form submission, make sure inputs in frontend have the correct cleannames

  const isGeschaeftskunde = kundentyp === KUNDENTYP_GESCHAEFT;
  const isBestandskunde = kundenstatus === KUNDENSTATUS_BESTAND;

  function mod97(digital: number | string) {
    const digi = digital.toString();
    let checksum: number | string = digi.slice(0, 2);
    let fragment = '';
    for (let offset = 2; offset < digi.length; offset += 7) {
      fragment = checksum + digi.substring(offset, offset + 7);
      checksum = parseInt(fragment, 10) % 97;
    }
    return checksum;
  }

  const validateIban = (input: string) => {
    const codeLengths = {
      DE: 22,
    };
    const iban = (input ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
    if (!code || iban.length !== (codeLengths as any)[code[1]]) {
      return false;
    }
    const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter: string) =>
      (letter.charCodeAt(0) - 55).toString(),
    );
    return mod97(digits) === 1;
  };

  return (
    <div className='form-page'>
      <div
        className={clsx({
          'hero-content': bannerImage,
        })}
      >
        {bannerImage ? (
          <BannerImage image={bannerImage} mobileImage={mobileBannerImage} />
        ) : (
          <div className='banner-image banner-image__small' />
        )}
        <Hero title={title} subtitle={subheader} text={subtitle} svg={svg} />
      </div>
      {beforeForm ? <Streamfields fields={beforeForm} /> : null}
      <form
        id='formular'
        className='form'
        onSubmit={async event => {
          event.preventDefault();
          setWasSubmitted(true);
          const formData = new FormData(event.currentTarget);

          const data: Record<string, FormDataEntryValue | FormDataEntryValue[]> =
            Object.fromEntries(formData);

          formFields.forEach(field => {
            if (field.field_type !== 'checkboxes') return;
            data[field.clean_name] = formData.getAll(field.clean_name);
          });

          // at least one Ladekarte must be selected (the Anzahl of a selected
          // card is enforced natively via the conditionally-rendered required field)
          const atLeastOneProduct = ladekarte || ladekarteRoaming;
          setProductError(!atLeastOneProduct);

          if (atLeastOneProduct && validateIban(data.iban as string)) {
            try {
              setStatus('loading');
              setErrors(null);
              const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/v2/forms/`, {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  page: formFields?.[0]?.page_id,
                  form_data: JSON.stringify(data),
                }),
              });

              if (res.ok) {
                setStatus('success');
              } else {
                const formErrors = (await res.json()) as FormApiErrors;
                setErrors(formErrors);
                const elem = document.getElementById(Object.keys(formErrors)[0]);
                elem?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                setStatus('error');
              }
            } catch {
              setStatus('error');
            }
          } else if (!atLeastOneProduct) {
            const elem = document.getElementById('stadtwerkedrive_ladekarte');
            elem?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          } else {
            const elem = document.getElementById('iban');
            elem?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          }
        }}
      >
        <div
          className={clsx('form-field', { 'form-field--error': productError })}
          key={22}
          style={{ width: '100%' }}
        >
          <Checkbox
            id='stadtwerkedrive_ladekarte'
            value='checked'
            label='Stadtwerkedrive Ladekarte'
            checked={ladekarte}
            onCheckedChange={checked => {
              setLadekarte(checked === true);
              setProductError(false);
            }}
          />
        </div>
        {ladekarte ? (
          <div
            className={clsx('form-field', { 'form-field--error': errors?.anzahl_ladekarte })}
            key={23}
            style={{ width: '37%' }}
          >
            <NumberField
              id={23}
              clean_name='anzahl_ladekarte'
              label='Anzahl'
              field_type='number'
              help_text=''
              sort_order={23}
              fieldset=''
              width={37}
              fields={[]}
              choices=''
              required
            />
          </div>
        ) : null}

        <div
          className={clsx('form-field', { 'form-field--error': productError })}
          key={24}
          style={{ width: '100%' }}
        >
          <Checkbox
            id='stadtwerkedrive_ladekarte_mit_roaming_option'
            value='checked'
            label='Stadtwerkedrive Ladekarte mit Roaming-Option'
            checked={ladekarteRoaming}
            onCheckedChange={checked => {
              setLadekarteRoaming(checked === true);
              setProductError(false);
            }}
          />
        </div>
        {ladekarteRoaming ? (
          <div
            className={clsx('form-field', {
              'form-field--error': errors?.anzahl_ladekarte_mit_roaming_option,
            })}
            key={25}
            style={{ width: '37%' }}
          >
            <NumberField
              id={25}
              clean_name='anzahl_ladekarte_mit_roaming_option'
              label='Anzahl'
              field_type='number'
              help_text=''
              sort_order={25}
              fieldset=''
              width={37}
              fields={[]}
              choices=''
              required
            />
          </div>
        ) : null}

        {productError ? (
          <p className='form-field__error-message' style={{ width: '100%' }}>
            Bitte wählen Sie mindestens eine Ladekarte aus und geben Sie die gewünschte Anzahl an.
          </p>
        ) : null}

        <div
          className={clsx('form-field form-field--mt', { 'form-field--error': errors?.kundentyp })}
          key={1}
          style={{ width: '100%' }}
        >
          <RadioField
            id={1}
            choices={`${KUNDENTYP_PRIVAT},${KUNDENTYP_GESCHAEFT}`}
            clean_name='kundentyp'
            label='Kundentyp'
            default_value={KUNDENTYP_PRIVAT}
            field_type='radio'
            help_text=''
            sort_order={1}
            fieldset=''
            width={100}
            fields={[]}
            isCustomForm
            required
            setCurrentSelected={setKundentyp}
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.kundenstatus })}
          key={2}
          style={{ width: '100%' }}
        >
          <RadioField
            id={2}
            choices={`${KUNDENSTATUS_BESTAND},${KUNDENSTATUS_NEU}`}
            clean_name='kundenstatus'
            label='Kundenstatus'
            default_value={KUNDENSTATUS_NEU}
            field_type='radio'
            help_text=''
            sort_order={2}
            fieldset=''
            width={100}
            fields={[]}
            isCustomForm
            required
            setCurrentSelected={setKundenstatus}
          />
        </div>

        {isBestandskunde ? (
          <div
            className={clsx('form-field form-field--mt', {
              'form-field--error': errors?.kundennummer,
            })}
            key={3}
            style={{ width: '52%' }}
          >
            <SingleLineField
              id={3}
              clean_name='kundennummer'
              label='Kundennummer'
              field_type='singleline'
              help_text='Sie finden Ihre Kundenummer auf Ihrer Energierechnung.'
              sort_order={3}
              fieldset=''
              width={52}
              fields={[]}
              choices=''
              required
              min_chars={10}
              max_chars={10}
            />
          </div>
        ) : null}

        {isGeschaeftskunde ? (
          <>
            <div
              className={clsx('form-field', { 'form-field--error': errors?.firma })}
              key={4}
              style={{ width: '48%' }}
            >
              <SingleLineField
                id={4}
                clean_name='firma'
                label='Firma'
                field_type='singleline'
                help_text=''
                sort_order={4}
                fieldset=''
                width={48}
                fields={[]}
                choices=''
                required={false}
              />
            </div>
            <div
              className={clsx('form-field', { 'form-field--error': errors?.umsatzsteuer_id })}
              key={5}
              style={{ width: '48%' }}
            >
              <SingleLineField
                id={5}
                clean_name='umsatzsteuer_id'
                label='Umsatzsteuer ID'
                field_type='singleline'
                help_text=''
                sort_order={5}
                fieldset=''
                width={48}
                fields={[]}
                choices=''
                required
              />
            </div>
          </>
        ) : null}
        <div
          className={clsx('form-field', { 'form-field--error': errors?.anrede })}
          key={6}
          style={{ width: '52%' }}
        >
          <SelectField
            id={6}
            choices='Frau,Herr,Divers,keine Angabe'
            clean_name='anrede'
            label='Anrede'
            default_value='Frau'
            field_type='dropdown'
            help_text=''
            sort_order={6}
            fieldset=''
            width={52}
            fields={[]}
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.vorname })}
          key={7}
          style={{ width: '48%' }}
        >
          <SingleLineField
            id={7}
            clean_name='vorname'
            label='Vorname'
            field_type='singleline'
            help_text=''
            sort_order={7}
            fieldset=''
            width={48}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.nachname })}
          key={8}
          style={{ width: '48%' }}
        >
          <SingleLineField
            id={8}
            clean_name='nachname'
            label='Nachname'
            field_type='singleline'
            help_text=''
            sort_order={8}
            fieldset=''
            width={48}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.e_mail_adresse })}
          key={9}
          style={{ width: '48%' }}
        >
          <EmailField
            id={9}
            clean_name='e_mail_adresse'
            label='E-Mail Adresse'
            field_type='email'
            help_text=''
            sort_order={9}
            fieldset=''
            width={48}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.telefonnummer })}
          key={10}
          style={{ width: '48%' }}
        >
          <SingleLineField
            id={10}
            clean_name='telefonnummer'
            label='Telefonnummer'
            field_type='singleline'
            help_text=''
            sort_order={10}
            fieldset=''
            width={48}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.straxdfe })}
          key={11}
          style={{ width: '60%' }}
        >
          <SingleLineField
            id={11}
            clean_name='straxdfe'
            label='Straße'
            field_type='singleline'
            help_text=''
            sort_order={11}
            fieldset=''
            width={60}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.hausnummer })}
          key={12}
          style={{ width: '37%' }}
        >
          <SingleLineField
            id={12}
            clean_name='hausnummer'
            label='Hausnummer'
            field_type='singleline'
            help_text=''
            sort_order={12}
            fieldset=''
            width={37}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.plz })}
          key={13}
          style={{ width: '37%' }}
        >
          <SingleLineField
            id={13}
            clean_name='plz'
            label='PLZ'
            field_type='singleline'
            help_text=''
            sort_order={13}
            fieldset=''
            width={37}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.ort })}
          key={14}
          style={{ width: '60%' }}
        >
          <SingleLineField
            id={14}
            clean_name='ort'
            label='Ort'
            field_type='singleline'
            help_text=''
            sort_order={14}
            fieldset=''
            width={60}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.freischaltcode })}
          key={15}
          style={{ width: '100%' }}
        >
          <SingleLineField
            id={15}
            clean_name='freischaltcode'
            label='Freischaltcode'
            field_type='singleline'
            help_text='Sie haben einen Freischaltcode erhalten? Tragen Sie den Code hier ein.'
            sort_order={15}
            fieldset=''
            width={100}
            fields={[]}
            choices=''
            required={false}
          />
        </div>

        <h2 style={{ width: '100%', marginTop: '2rem' }}>Zahlungsweise</h2>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.kontoinhaber })}
          key={16}
          style={{ width: '23%' }}
        >
          <SingleLineField
            id={16}
            clean_name='kontoinhaber'
            label='Kontoinhaber'
            field_type='singleline'
            help_text=''
            sort_order={16}
            fieldset=''
            width={23}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.iban })}
          key={17}
          style={{ width: '23%' }}
        >
          <SingleLineField
            id={17}
            clean_name='iban'
            label='IBAN'
            field_type='singleline'
            help_text=''
            sort_order={17}
            fieldset=''
            validation={validateIban}
            width={23}
            fields={[]}
            choices=''
            required
            submitted={wasSubmitted}
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.bic })}
          key={18}
          style={{ width: '23%' }}
        >
          <SingleLineField
            id={18}
            clean_name='bic'
            label='BIC'
            field_type='singleline'
            help_text=''
            sort_order={18}
            fieldset=''
            width={23}
            fields={[]}
            choices=''
            required
          />
        </div>
        <div
          className={clsx('form-field', { 'form-field--error': errors?.bankname })}
          key={19}
          style={{ width: '23%' }}
        >
          <SingleLineField
            id={19}
            clean_name='bankname'
            label='Bankname'
            field_type='singleline'
            help_text=''
            sort_order={19}
            fieldset=''
            width={23}
            fields={[]}
            choices=''
            required
          />
        </div>

        <div
          className={clsx('form-field form-field--mt', {
            'form-field--error': errors?.sepa_lastschrift,
          })}
          key={26}
          style={{ width: '100%' }}
        >
          <CheckBoxField
            id={26}
            choices=''
            clean_name='sepa_lastschrift'
            label='Hiermit erteile ich ein SEPA-Lastschriftmandat'
            field_type='checkbox'
            help_text='Mit dem Absenden des Formulares ermächtige(n) ich/wir die Stadtwerke Witten GmbH Zahlungen von meinem/unserem Konto mittels Lastschrift einzuziehen. Zugleich weise(n) ich/wir mein/unser Kreditinstitut an, die von der Stadtwerke Witten GmbH auf mein/unser Konto gezogenen Lastschriften einzulösen. Gläubigeridentifikationsnummer: DE58ZZZ00000316018'
            sort_order={26}
            fieldset=''
            width={100}
            fields={[]}
            required
          />
        </div>
        <div
          className={clsx('form-field', {
            'form-field--error': errors?.agb_und_preisblatt_gelesen_und_akzeptiert,
          })}
          key={20}
          style={{ width: '100%' }}
        >
          <CheckBoxField
            id={20}
            choices=''
            clean_name='agb_und_preisblatt_gelesen_und_akzeptiert'
            label='AGB und Preisblatt gelesen und akzeptiert.'
            field_type='checkbox'
            help_text='Ich habe die <a href="https://www.stadtwerke-witten.de/media/documents/AGB_Ladekarte_gueltig_ab_01.04.2024.pdf" target="_blank" download>Allgemeinen Bedingungen</a> und Preisblatt zur Ladekarte Stadtwerk<strong>edrive</strong> zur Kenntnis genommen und akzeptiere diese.'
            sort_order={20}
            fieldset=''
            width={100}
            fields={[]}
            required
          />
        </div>
        <div
          className={clsx('form-field', {
            'form-field--error': errors?.einwilligung_zur_datenverarbeitung,
          })}
          key={21}
          style={{ width: '100%' }}
        >
          <CheckBoxField
            id={21}
            choices=''
            clean_name='einwilligung_zur_datenverarbeitung'
            label='Einwilligung zur Datenverarbeitung'
            field_type='checkbox'
            help_text='Hiermit stimme ich der Verarbeitung meiner Daten zu. Ich kann meine Zustimmung jederzeit mit Wirkung für die Zukunft widerrufen. Weitere Informationen in der <a href="/datenschutz" target="_blank">Datenschutzerklärung</a>.'
            sort_order={21}
            fieldset=''
            width={100}
            fields={[]}
            required
          />
        </div>
        <Honeypot />
        <p>* Pflichtfelder</p>
        {status === 'success' ? (
          <div className='message message--success'>
            <RawHtml html={thankYouText} />
          </div>
        ) : null}
        {!errors && status === 'error' ? (
          <div className='message message--error'>
            <p>Leider ist etwas schief gegangen... Bitte versuchen Sie es später erneut...</p>
            <p>
              Alternativ können Sie Ihre Anfrage auch direkt an die E-Mail-Adresse{' '}
              <a href='mailto:kundenservice@stadtwerke-witten.de'>
                kundenservice@stadtwerke-witten.de
              </a>{' '}
              senden.
            </p>
          </div>
        ) : null}
        {errors && status === 'error' ? (
          <div className='message message--error'>
            <p>Ihre Anfrage konnte nicht bearbeitet werden, bitte füllen Sie alle Felder aus.</p>
            <p>
              Alternativ können Sie Ihre Anfrage auch direkt an die E-Mail-Adresse{' '}
              <a href='mailto:kundenservice@stadtwerke-witten.de'>
                kundenservice@stadtwerke-witten.de
              </a>{' '}
              senden.
            </p>
          </div>
        ) : null}
        <div className='form-page__submit'>
          <Button
            primary
            isSubmit
            disabled={status === 'loading' || status === 'success'}
            className='form__submit'
          >
            Absenden
          </Button>
        </div>
      </form>
      {afterForm ? <Streamfields fields={afterForm} /> : null}
    </div>
  );
}

export default basePageWrap<Props>(LadekarteBestellenPage);

export const getStaticProps: GetStaticProps = async context => {
  const { params } = context;
  let path = params?.path ?? [];
  if (Array.isArray(path)) path = path.join('/');

  try {
    const { json: pageData } = await getPage('/ladekarte-bestellen');
    const [{ json: footerData }, { json: headerData }, { json: menuData }, { json: openingHours }] =
      await Promise.all([getFooter(), getHeader(), getMenu(), getOpeningHours()]);

    const blocksLoaderData = new Map<string, unknown>();
    let streamfields: Streamfield[] = [
      // @ts-expect-error
      ...(pageData.content ?? []),
      // @ts-expect-error
      ...(pageData.before_form ?? []),
      // @ts-expect-error
      ...(pageData.after_form ?? []),
    ];

    const containerFields = streamfields.filter(field => field.type === 'container');
    // eslint-disable-next-line no-restricted-syntax
    for (const field of containerFields) {
      streamfields = streamfields.concat(field?.value?.content ?? []);
    }

    if (streamfields.length) {
      // eslint-disable-next-line no-restricted-syntax
      for (const streamfield of streamfields) {
        const blockLoader = loaders[streamfield.type];
        // eslint-disable-next-line no-await-in-loop
        const data = await blockLoader?.(streamfield.value);
        if (data) blocksLoaderData.set(streamfield.id, data);
      }
    }

    return {
      props: {
        ...pageData,
        footerData,
        headerData,
        menuData,
        openingHours,
      },
      revalidate: 3600,
    };
  } catch (err) {
    if (!(err instanceof WagtailApiResponseError)) {
      throw err;
    }

    if (err.response.status >= 500) {
      throw err;
    }

    return { notFound: true, revalidate: 60 };
  }
};
